import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SPLIT_TYPE, getSplitTypeLabel, type ExpenseSplitView } from "@/lib/types";
import { loadNewExpense, createExpenseFn } from "@/lib/server-fns/expenses";
import {
  Container,
  Heading,
  Button,
  Input,
  HStack,
  Card,
  Text,
  VStack,
  NativeSelect,
  NumberInput,
  Field,
  Flex,
  Grid,
  SegmentGroup,
  For,
  Collapsible,
} from "@chakra-ui/react";
import { showErrorToast } from "@/lib/toast-utils";
import { buildParticipantOptions, getDisplayName } from "@/lib/user-utils";
import { CURRENCY_ICONS, SUPPORTED_CURRENCIES } from "@/lib/constants";
import { LuArrowLeft, LuChevronDown } from "react-icons/lu";
import { useState } from "react";
import { SplitParticipants } from "@/components/split-participants";
import { InputGroup } from "@/components/ui/input-group";

export const Route = createFileRoute("/_authenticated/expenses/new")({
  loader: () => loadNewExpense(),
  component: ExpenseNew,
});

function ExpenseNew() {
  const { friends, currentUser, defaultCurrency } = Route.useLoaderData();
  const navigate = useNavigate();
  const [splitType, setSplitType] = useState<string>(SPLIT_TYPE.EQUAL);
  const [splits, setSplits] = useState<ExpenseSplitView[]>([
    { userId: currentUser.id, amount: "" },
  ]);
  const [date, setDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [paidBy, setPaidBy] = useState(currentUser.id);
  const [amount, setAmount] = useState("");

  const participantOptions = buildParticipantOptions({ currentUser, friends });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const isoDate = new Date(date).toISOString().split("T")[0];

      await createExpenseFn({
        data: {
          description: formData.get("description") as string,
          amount: formData.get("amount") as string,
          currency,
          paidBy,
          splitType,
          splits,
          date: isoDate,
        },
      });

      navigate({ to: "/expenses" });
    } catch (error) {
      showErrorToast("Failed to create expense", error);
    }
  };

  return (
    <Container maxW={{ base: "md", md: "3xl" }} px={{ base: "0", md: "4" }}>
      <HStack mb="6" px={{ base: "4", md: "0" }}>
        <Button asChild variant="ghost">
          <Link to="/expenses">
            <LuArrowLeft size={16} />
            Back to Expenses
          </Link>
        </Button>
      </HStack>

      <form onSubmit={handleSubmit}>
        <input type="hidden" name="splitType" value={splitType} />
        <input type="hidden" name="currency" value={currency} />
        <input type="hidden" name="paidBy" value={paidBy} />

        {/* Collapsible settings */}
        <Collapsible.Root mb="4">
          <Collapsible.Trigger asChild>
            <Button variant="outline" size="lg" w="full" justifyContent="space-between">
              <Text fontSize="sm">
                {currency} · {getDisplayName(paidBy, { currentUser, friends })} paid ·{" "}
                {getSplitTypeLabel(splitType)}
              </Text>
              <Collapsible.Indicator
                transition="transform 0.2s"
                _open={{ transform: "rotate(180deg)" }}
              >
                <LuChevronDown />
              </Collapsible.Indicator>
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <Card.Root variant="outline" mt="2">
              <Card.Body p={{ base: "4", md: "6" }}>
                <VStack gap="4" align="stretch">
                  <Field.Root required>
                    <Field.Label>Currency</Field.Label>
                    <SegmentGroup.Root
                      value={currency}
                      onValueChange={(details) => setCurrency(details.value ?? defaultCurrency)}
                      width="full"
                      css={{
                        "--segment-indicator-bg": "colors.loonie.solid",
                        "--segment-indicator-shadow": "shadows.md",
                      }}
                    >
                      <SegmentGroup.Indicator />
                      <For each={SUPPORTED_CURRENCIES}>
                        {(code) => (
                          <SegmentGroup.Item key={code} value={code}>
                            <SegmentGroup.ItemText>
                              <HStack gap="1.5">
                                {CURRENCY_ICONS[code]}
                                {code}
                              </HStack>
                            </SegmentGroup.ItemText>
                            <SegmentGroup.ItemHiddenInput />
                          </SegmentGroup.Item>
                        )}
                      </For>
                    </SegmentGroup.Root>
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>Paid By</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={paidBy}
                        onChange={(e) => setPaidBy(e.target.value)}
                      >
                        {participantOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Split Type</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={splitType}
                        onChange={(e) => setSplitType(e.target.value)}
                      >
                        <option value={SPLIT_TYPE.EQUAL}>Split Equally</option>
                        <option value={SPLIT_TYPE.EXACT}>Exact Amounts</option>
                        <option value={SPLIT_TYPE.PERCENTAGE}>By Percentage</option>
                        <option value={SPLIT_TYPE.SHARES}>By Shares</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </VStack>
              </Card.Body>
            </Card.Root>
          </Collapsible.Content>
        </Collapsible.Root>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="4" alignItems="start">
          {/* Left: Expense details */}
          <Card.Root variant="outline">
            <Card.Body p={{ base: "4", md: "6" }}>
              <Heading as="h2" size="lg" mb="6">
                Add Expense
              </Heading>

              <VStack gap="4" align="stretch">
                <Field.Root required>
                  <Field.Label>Description</Field.Label>
                  <Input name="description" placeholder="Dinner, Groceries, Rent, etc." />
                </Field.Root>

                <Field.Root required>
                  <Field.Label>Date</Field.Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field.Root>

                <Field.Root required>
                  <Field.Label>Total Amount</Field.Label>
                  <Field.HelperText>Enter the total expense amount</Field.HelperText>
                  <NumberInput.Root
                    name="amount"
                    min={0.01}
                    step={0.01}
                    onValueChange={(details) => setAmount(details.value)}
                  >
                    <NumberInput.Control />
                    <InputGroup startElement={CURRENCY_ICONS[currency]}>
                      <NumberInput.Input placeholder="25.00" />
                    </InputGroup>
                  </NumberInput.Root>
                </Field.Root>
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Right: Split participants */}
          <Card.Root variant="outline">
            <Card.Body p={{ base: "4", md: "6" }}>
              <Text fontSize="sm" fontWeight="medium" mb="4">
                Split With
              </Text>
              <SplitParticipants
                currentUser={currentUser}
                friends={friends}
                splits={splits}
                splitType={splitType}
                onSplitsChange={setSplits}
                totalAmount={amount}
                currency={currency}
              />
            </Card.Body>
          </Card.Root>
        </Grid>

        <Flex
          direction={{ base: "column-reverse", md: "row" }}
          justify="flex-end"
          gap="3"
          mt="4"
          px={{ base: "4", md: "0" }}
        >
          <Button asChild variant="ghost" w={{ base: "full", md: "auto" }}>
            <Link to="/expenses">Cancel</Link>
          </Button>
          <Button type="submit" w={{ base: "full", md: "auto" }}>
            Add Expense
          </Button>
        </Flex>
      </form>
    </Container>
  );
}
